import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  User, 
  MapPin, 
  Mail, 
  Calendar,
  Edit,
  Trash2,
  Users,
  ExternalLink,
  Loader2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusLabels = {
  urgent: { label: 'Urgent', variant: 'destructive' },
  en_cours: { label: 'En cours', variant: 'default' },
  resolu: { label: 'Résolu', variant: 'secondary' }
};

const categoryLabels = {
  controle: 'Contrôle',
  mise_en_demeure: 'Mise en demeure',
  proces: 'Procès',
  expulsion: 'Expulsion'
};

export default function Profile() {
  const navigate = useNavigate();
  const { user, token, loading: authLoading } = useAuth();
  const [situations, setSituations] = useState([]);
  const [supports, setSupports] = useState({});
  const [loading, setLoading] = useState(true);
  
  // Edit dialog state
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedSituation, setSelectedSituation] = useState(null);
  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    status: '',
    support_link: '',
    contact_email: '',
    needs_support: true
  });
  const [savingEdit, setSavingEdit] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/connexion');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user) {
      fetchUserSituations();
    }
  }, [user]);

  // Fill edit form when opening dialog
  useEffect(() => {
    if (showEditDialog && selectedSituation) {
      setEditForm({
        title: selectedSituation.title || '',
        description: selectedSituation.description || '',
        status: selectedSituation.status || 'en_cours',
        support_link: selectedSituation.support_link || '',
        contact_email: selectedSituation.contact_email || '',
        needs_support: selectedSituation.needs_support ?? true
      });
    }
  }, [showEditDialog, selectedSituation]);

  const fetchUserSituations = async () => {
    try {
      const response = await axios.get(`${API}/situations`);
      const userSituations = response.data.filter(s => s.user_id === user.id);
      setSituations(userSituations);

      // Fetch supports for each situation
      const supportsData = {};
      for (const situation of userSituations) {
        try {
          const supportsResponse = await axios.get(`${API}/situations/${situation.id}/supports`);
          supportsData[situation.id] = supportsResponse.data;
        } catch (error) {
          console.error(`Failed to fetch supports for ${situation.id}:`, error);
          supportsData[situation.id] = [];
        }
      }
      setSupports(supportsData);
    } catch (error) {
      console.error('Failed to fetch situations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditSituation = (situation) => {
    setSelectedSituation(situation);
    setShowEditDialog(true);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    setSavingEdit(true);
    try {
      await axios.put(`${API}/situations/${selectedSituation.id}`, {
        title: editForm.title,
        description: editForm.description,
        status: editForm.status,
        support_link: editForm.support_link || null,
        contact_email: editForm.contact_email || null,
        needs_support: editForm.needs_support
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Situation mise à jour !');
      setShowEditDialog(false);
      fetchUserSituations();
    } catch (error) {
      console.error('Update error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeleteSituation = async (situationId) => {
    try {
      await axios.delete(`${API}/situations/${situationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Situation supprimée');
      fetchUserSituations();
    } catch (error) {
      console.error('Failed to delete situation:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  if (authLoading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="profile-page">
      {/* Header */}
      <div className="bg-muted/30 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2">
            Mon profil
          </h1>
          <p className="text-muted-foreground">
            Gérez vos informations et vos situations référencées.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* User info */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Informations
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <User className="w-10 h-10 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold text-center">{user.name}</h3>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Mail className="w-4 h-4" />
                    {user.email}
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Calendar className="w-4 h-4" />
                    Membre depuis {new Date(user.created_at).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="mt-4">
              <CardContent className="pt-6 text-center">
                <div className="text-3xl font-bold text-primary">{situations.length}</div>
                <div className="text-sm text-muted-foreground">situation{situations.length > 1 ? 's' : ''} référencée{situations.length > 1 ? 's' : ''}</div>
              </CardContent>
            </Card>
          </div>

          {/* Situations */}
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Mes situations</h2>
              <Button asChild>
                <a href="/carte">Ajouter une situation</a>
              </Button>
            </div>

            {loading ? (
              <div className="space-y-4">
                {[1, 2].map((i) => (
                  <Card key={i} className="animate-pulse">
                    <CardContent className="py-6">
                      <div className="h-5 bg-muted rounded w-3/4 mb-2" />
                      <div className="h-4 bg-muted rounded w-1/2" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : situations.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <MapPin className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">Aucune situation</h3>
                  <p className="text-muted-foreground mb-4">
                    Vous n'avez pas encore référencé de situation sur la carte.
                  </p>
                  <Button asChild>
                    <a href="/carte">Référencer ma situation</a>
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {situations.map((situation) => (
                  <Card key={situation.id} data-testid={`my-situation-${situation.id}`}>
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant={statusLabels[situation.status]?.variant || 'default'}>
                              {statusLabels[situation.status]?.label}
                            </Badge>
                            <Badge variant="outline">{categoryLabels[situation.category]}</Badge>
                          </div>
                          <CardTitle className="text-lg">{situation.title}</CardTitle>
                          <CardDescription className="flex items-center gap-1 mt-1">
                            <MapPin className="w-3 h-3" />
                            {situation.address || situation.location}
                          </CardDescription>
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => handleEditSituation(situation)}
                            data-testid={`edit-situation-${situation.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => handleDeleteSituation(situation.id)}
                            data-testid={`delete-situation-${situation.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                        {situation.description}
                      </p>
                      
                      <div className="flex items-center gap-4 text-sm">
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Users className="w-4 h-4" />
                          {situation.support_count} soutien{situation.support_count > 1 ? 's' : ''}
                        </div>
                        {(situation.support_link || situation.fundraiser_url) && (
                          <a 
                            href={situation.support_link || situation.fundraiser_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-primary hover:underline"
                          >
                            <ExternalLink className="w-4 h-4" />
                            Lien de soutien
                          </a>
                        )}
                      </div>

                      {/* Supports received */}
                      {supports[situation.id] && supports[situation.id].length > 0 && (
                        <div className="mt-4 pt-4 border-t">
                          <h4 className="font-medium text-sm mb-2">Messages de soutien reçus :</h4>
                          <div className="space-y-2 max-h-40 overflow-y-auto">
                            {supports[situation.id].map((support) => (
                              <div key={support.id} className="bg-muted/50 p-3 rounded-sm text-sm">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="font-medium">{support.name}</span>
                                  <div className="flex items-center gap-2">
                                    <Badge variant="outline" className="text-xs">
                                      {support.support_type === 'presence' ? 'Présence' : 
                                       support.support_type === 'relay' ? 'Relais' : 
                                       support.support_type === 'soutien_juridique' ? 'Juridique' : 'Aide'}
                                    </Badge>
                                    {support.conversation_id && (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => navigate(`/messages?conversation=${support.conversation_id}`)}
                                        className="h-6 px-2"
                                        data-testid={`reply-support-${support.id}`}
                                      >
                                        <Mail className="w-3 h-3 mr-1" />
                                        Répondre
                                      </Button>
                                    )}
                                  </div>
                                </div>
                                <p className="text-muted-foreground text-xs mb-1">{support.email}</p>
                                <p className="text-muted-foreground">{support.message}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Situation Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5" />
              Modifier ma situation
            </DialogTitle>
          </DialogHeader>
          {selectedSituation && (
            <form onSubmit={handleSaveEdit} className="space-y-4">
              <div>
                <Label htmlFor="edit-title">Titre *</Label>
                <Input
                  id="edit-title"
                  value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  required
                  data-testid="edit-title-input"
                />
              </div>
              <div>
                <Label htmlFor="edit-description">Description *</Label>
                <Textarea
                  id="edit-description"
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  required
                  rows={4}
                  data-testid="edit-description-input"
                />
              </div>
              <div>
                <Label htmlFor="edit-status">Statut</Label>
                <Select
                  value={editForm.status}
                  onValueChange={(value) => setEditForm({ ...editForm, status: value })}
                >
                  <SelectTrigger id="edit-status" data-testid="edit-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="urgent">Urgent</SelectItem>
                    <SelectItem value="en_cours">En cours</SelectItem>
                    <SelectItem value="resolu">Résolu</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="edit-support-link">Lien de pétition / cagnotte</Label>
                <Input
                  id="edit-support-link"
                  type="url"
                  value={editForm.support_link}
                  onChange={(e) => setEditForm({ ...editForm, support_link: e.target.value })}
                  placeholder="https://..."
                  data-testid="edit-support-link-input"
                />
              </div>
              <div>
                <Label htmlFor="edit-email">Email de contact</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={editForm.contact_email}
                  onChange={(e) => setEditForm({ ...editForm, contact_email: e.target.value })}
                  placeholder="email@example.com"
                  data-testid="edit-email-input"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="edit-needs-support"
                  checked={editForm.needs_support}
                  onChange={(e) => setEditForm({ ...editForm, needs_support: e.target.checked })}
                  className="rounded"
                  data-testid="edit-needs-support-checkbox"
                />
                <Label htmlFor="edit-needs-support" className="cursor-pointer">
                  Je recherche du soutien
                </Label>
              </div>
              <div className="flex gap-2 pt-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowEditDialog(false)}
                  className="flex-1"
                >
                  Annuler
                </Button>
                <Button 
                  type="submit" 
                  className="flex-1" 
                  disabled={savingEdit}
                  data-testid="save-edit-button"
                >
                  {savingEdit ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sauvegarde...
                    </>
                  ) : (
                    'Enregistrer'
                  )}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
